import { useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Camera, X } from 'lucide-react'
import type { Event } from '@/types'
import { formatCardDateTime } from '@/lib/utils'
import { criticalReasonText, type Translate } from '@/lib/criticalEvents'
import { eventOpenTarget } from '@/lib/navigation'
import { showInList } from '@/lib/alertRowLink'
import { useAlertLinkHandlers } from '@/store/alertLinkStore'
import { useElementHeightVar } from '@/hooks/useElementHeightVar'
import { useEventTitle } from '@/hooks/useEventTitle'
import { UnreadRowLight } from '@/components/events/UnreadRowLight'
import { useCriticalAlertsContext } from '@/components/layout/CriticalAlertsContext'

/** The gap kept below the column when the normal toasts start under it. */
const COLUMN_GAP_PX = 10

function CriticalPopup({ event }: { event: Event }) {
  const { t } = useTranslation('common')
  const navigate = useNavigate()
  const location = useLocation()
  const title = useEventTitle()
  const { acknowledge, acknowledgingId, hidePopup } = useCriticalAlertsContext()
  const reason = criticalReasonText(event, t as unknown as Translate)
  const camera = event.camera_name ?? ''
  const link = useAlertLinkHandlers(event.id)

  const onView = () => {
    const { to, options } = eventOpenTarget(location, event.id)
    navigate(to, options)
  }

  return (
    <div role="group" aria-label={t('critical.popup.label', { camera })} className="crit-pop" data-critical-popup={event.id} {...link}>
      <UnreadRowLight variant="critical" />
      <div className="crit-pop-thumb" aria-hidden="true">
        {event.thumbnail_url
          ? <img src={event.thumbnail_url} alt="" loading="lazy" />
          : <Camera className="h-5 w-5" />}
      </div>
      <div className="crit-pop-body">
        <span className="crit-pop-label">
          <b>{t('critical.label')}</b>{reason ? <> · <span>{reason}</span></> : null}
        </span>
        <span className="crit-pop-cam" title={camera}>{camera}</span>
        {/* a WebSocket placeholder has no real time yet (occurred_at is this browser's "now"): none until it arrives */}
        <span className="crit-pop-meta">{event._seeded ? title(event) : `${formatCardDateTime(event.occurred_at)} · ${title(event)}`}</span>
        <div className="crit-pop-actions">
          <button type="button" className="crit-pop-btn" onClick={onView}>{t('critical.popup.view')}</button>
          <button
            type="button"
            className="crit-pop-btn crit-pop-btn-solid"
            onClick={() => acknowledge(event.id)}
            disabled={acknowledgingId === event.id}
          >
            {t('critical.acknowledge')}
          </button>
          {/* #413 (final review M2) — last: Acknowledge keeps its place, and only this button wraps on a narrow popup */}
          <button
            type="button"
            className="crit-pop-btn alert-show-in-list"
            onClick={(e) => showInList(event.id, onView, { focus: e.detail === 0 })}
          >
            {t('alertLink.showInList')}
          </button>
        </div>
      </div>
      <button type="button" className="crit-pop-x" onClick={() => hidePopup(event.id)} aria-label={t('critical.popup.hide')}>
        <X className="h-3 w-3" aria-hidden />
      </button>
    </div>
  )
}

/**
 * #294 redesign (CP 294 Section 1.1) — the critical popups, on every page. Rendered once from MainLayout in
 * its OWN fixed column (`.crit-popups`, below the alert bar; not inside PeekCards, which is mounted on
 * /login too), reading the one critical alerts controller: at most 3, newest first, then "+ N more critical
 * alerts" (opens the Dashboard). The popups never hide by themselves (the controller latches them). They are
 * not live regions: the alert bar announces. The column's height is published as --crit-popups-h so the
 * normal toasts start below it.
 */
export function CriticalPopupStack() {
  const { t } = useTranslation('common')
  const navigate = useNavigate()
  const { popups, morePopups } = useCriticalAlertsContext()
  const ref = useRef<HTMLDivElement>(null)
  const shown = popups.length > 0 || morePopups > 0
  useElementHeightVar(ref, '--crit-popups-h', shown, COLUMN_GAP_PX)
  if (!shown) return null

  return (
    <div ref={ref} className="crit-popups" data-testid="critical-popups">
      {popups.map((e) => <CriticalPopup key={e.id} event={e} />)}
      {morePopups > 0 && (
        <button type="button" className="crit-pop-more" onClick={() => navigate('/')}>
          {t('critical.popup.more', { count: morePopups })}
        </button>
      )}
    </div>
  )
}
